import { FC, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Stack, Typography } from '@mui/material';
import useImages from 'api/useImages';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';

import AcSelect from 'components/AcSelect/AcSelect';

import { defaultStoreTheme } from '../defaults';
import { StoreThemeFormValues } from '../storeTheme.types';

const DefaultProfileImgSelect: FC = () => {
  const { publisherId } = useParams();
  const pictures = useImages(publisherId).getImages;
  const { values, setFieldValue, handleChange, handleBlur } =
    useFormikContext<StoreThemeFormValues>();

  const items = useMemo(() => {
    const profileImgItems =
      pictures?.data?.filter(
        (p: any) => p.type === EAssetType.PLAYER_PROFILE_IMG
      ) || [];
    const defaultItem = {
      name: 'Default player img',
      url: defaultStoreTheme.defaultPlayerImage
    };
    const imgItemsList = [defaultItem, ...profileImgItems];

    return imgItemsList.map((img) => ({
      content: img.name,
      key: img.url,
      value: img.url,
      renderFunction: () => {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <img
              src={img.url}
              alt={img.name}
              style={{
                width: 30,
                maxHeight: 30
              }}
            />
            <Typography>{img.name}</Typography>
          </Stack>
        );
      }
    }));
  }, [pictures?.data]);

  const uploadConfig = useMemo(
    () => ({
      onUploadSuccess: async (url: string) => {
        await pictures.refetch();
        setFieldValue('defaultPlayerImage', url);
      },
      uploadType: EAssetType.PLAYER_PROFILE_IMG,
      uploadMessage: UploadsTooltips[EAssetType.PLAYER_PROFILE_IMG]
    }),
    [pictures, setFieldValue]
  );

  return (
    <AcSelect
      header="Default Player Profile Pic"
      name="defaultPlayerImage"
      value={values.defaultPlayerImage}
      dataTestBoxId="defaultPlayerImage"
      uploadBtnTestId="uploadDefaultPlayerImage"
      dataTestListId="defaultPlayerImageList"
      optionDataTestIdPrefix="defaultPlayerImageOption"
      uploadConfig={uploadConfig}
      items={items}
      onChange={handleChange}
      onBlur={handleBlur}
      tooltip="This will be the default player img"
    />
  );
};

export default DefaultProfileImgSelect;
